<template>
  <PageContainer v-if="data" :breadcrumbs="breadcrumbs">
    <h1 class="pageHeader">{{ data.pack.packName }}</h1>

    <div class="Body" v-if="data.pokemonSingleProducts.length">
      <div class="SummaryWrapper" v-for="product in data.pokemonSingleProducts" :key="product.inventory_product_id" >
        <Summary class="Summary" :data="product" :disable-view-transition />
      </div>
    </div>
  </PageContainer>
</template>

<script lang="ts">
import { definePageMeta, useSeoMeta, useHead, useRuntimeConfig } from '#imports'
import {
  defineNuxtComponent,
  useAsyncData,
  useNuxtApp,
  createError,
  useRouter,
} from "#app";
import { storeToRefs } from 'pinia'
import { DITokens } from "@/di";
import { computed } from 'vue'
import type { ResponseType as PackResponseType } from "@/server/api/pokemon/packs/[packId]/index.get";
import Summary from '@/components/Pokemon/Single/Summary'
import PageContainer from '@/containers/Page'
import { PokemonSeries } from '@/const/pokemon'

export default defineNuxtComponent({
  components: {
    Summary,
    PageContainer,
  },
  async setup() {
    const { public: { APP_ENV } } = useRuntimeConfig()

    definePageMeta({
      genre: 'pokemon',
      footer: {
        isShowSocialMedia: true,
        isShowShopSeries: true,
      }
    })

    const route = useRouter().currentRoute.value
    const { packId } = route.params;
    const { $container } = useNuxtApp();
    const cartStore = $container.resolve(DITokens.CartStore);
    const cartState = storeToRefs(cartStore);
    const disableViewTransition = computed(() => cartState.showCart.value) // カート表示中はview transitionをoff

    const { data, error } = await useAsyncData(
      `pokemonPackPagedata_${packId}`,
      async () => {
        const [{ pack }, pokemonSingleProducts] = await Promise.all([
          $fetch<PackResponseType>(`/api/pokemon/packs/${packId}`),
          $container
            .resolve(DITokens.ElasticsearchGateway)
            .getPokemonSingleProductsByPackId(packId as string),
        ]);

        return {
          pack,
          pokemonSingleProducts,
        };
      }
    );

    if (error.value || !data.value) {
      throw createError({
        statusCode: 404,
        statusMessage: "Page not found",
      });
    }

    // Set Breadcrumbs
    const seriesId = data.value.pack.series?.seriesId
    const seriesNameStr = typeof seriesId === 'number' ? PokemonSeries[seriesId] : 'シリーズ'
    const seriesPageLink = typeof seriesId === 'number' ? `/pokemon/series/${seriesId}` : undefined
    const packNameStr = data.value.pack.packName

    const title = packNameStr + ' - カーナベル'
    const description = `${packNameStr}のカードを状態別にどこよりも安く販売中！最速で翌日午前に入手可能！`

    useSeoMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      robots: APP_ENV === 'production' ? 'index,follow' : 'noindex,nofollow',
      twitterSite: '@ka_nabell_pcg',
      ogSiteName: 'ポケカ販売ならカーナベル！',
      ogUrl: `https://sell.ka-nabell.com/pokemon/series/${seriesId}/packs/${packId}`
    });

    useHead({
      link: [
        {
          rel: 'canonical',
          href: `https://sell.ka-nabell.com/pokemon/series/${seriesId}/packs/${packId}`
        }
      ]
    })

    const breadcrumbs = [
      {
        title: 'トレカ販売トップ',
        link: '/',
      },
      {
        title: 'ポケカ販売トップ',
        link: '/pokemon',
      },
      {
        title: seriesNameStr,
        link: seriesPageLink,
      },
      {
        title: packNameStr,
      },
    ]

    return {
      data,
      breadcrumbs,
      disableViewTransition
    };
  },
});
</script>

<style scoped>
.Body {
  width: 100%;
  display: flex;
  column-gap: 16px;
  row-gap: 20px;
  flex-wrap: wrap;
}

.SummaryWrapper {
  width: calc((100% - (16px * 2)) / 3);
}

.pageHeader {
  width: 100%;
}
</style>
